import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { IconCaretDown, IconCaretUp } from '../icon/icon'

// A ⋮ menu's frame (owner report 2026-10-13: "these menus look broken and
// cut off"). When a menu is taller than the room the page leaves it
// (floating.js hands that room over as --floating-max-height), the list
// scrolls INSIDE the frame: the frame's border, rounded corners and shadow
// stay put, and nothing reads as clipped.
//
// The edge that has more behind it says so: "3 more" with a caret, at the
// top once it has scrolled down, at the bottom while there are items below.
// Each is a strip over the list's edge (it never moves the items); a click
// on it scrolls a page that way. The counts are actions, not section
// headings, and an action counts as hidden once its middle is behind the
// strip or out of view.

// The strip's height in px, kept in step with .menu-more in menu.scss: an
// item under a strip is not in view.
export const MORE_STRIP = 26

// How many actions are out of view above and below. `items` are the
// actions' {top, bottom}; `view` the scroller's {top, bottom, scrollTop,
// scrollHeight, clientHeight}. At the very top nothing is above, at the very
// bottom nothing is below; elsewhere the strip on that edge covers its own
// height of the list.
export const hiddenCounts = (items, view) => {
  const atTop = view.scrollTop <= 1
  const atBottom = view.scrollTop + view.clientHeight >= view.scrollHeight - 1
  if (atTop && atBottom) return { above: 0, below: 0 }

  const topLine = view.top + (atTop ? 0 : MORE_STRIP)
  const bottomLine = view.bottom - (atBottom ? 0 : MORE_STRIP)
  let above = 0
  let below = 0
  for (const item of items) {
    const middle = (item.top + item.bottom) / 2
    if (!atTop && middle < topLine) above++
    else if (!atBottom && middle > bottomLine) below++
  }
  return { above, below }
}

const measureHidden = el => {
  const rect = el.getBoundingClientRect()
  const items = Array.from(el.querySelectorAll('[data-menu-action]')).map(item => item.getBoundingClientRect())
  return hiddenCounts(items, {
    top: rect.top,
    bottom: rect.bottom,
    scrollTop: el.scrollTop,
    scrollHeight: el.scrollHeight,
    clientHeight: el.clientHeight
  })
}

export const MenuScrollFrame = ({ children }) => {
  const ref = useRef(null)
  const [hidden, setHidden] = useState({ above: 0, below: 0 })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const next = measureHidden(el)
    setHidden(prev => (prev.above === next.above && prev.below === next.below ? prev : next))
  }, [])

  // Again whenever the room changes (floating-ui re-sizes the frame as the
  // page scrolls or resizes) or the list does.
  useLayoutEffect(() => {
    measure()
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [measure])

  const page = direction => e => {
    e.stopPropagation()
    const el = ref.current
    if (!el) return
    const by = Math.max(MORE_STRIP, el.clientHeight - 2 * MORE_STRIP) * direction
    if (el.scrollBy) el.scrollBy({ top: by, behavior: 'smooth' })
    else el.scrollTop += by
  }

  return (
    <div className="menu-frame" data-testid="menu-frame">
      <div ref={ref} className="menu-scroll" onScroll={measure}>
        {children}
      </div>
      {hidden.above > 0 &&
        <button type="button" tabIndex={-1} className="menu-more menu-more-above" onClick={page(-1)} data-testid="menu-more-above">
          <IconCaretUp /> {hidden.above} more
        </button>}
      {hidden.below > 0 &&
        <button type="button" tabIndex={-1} className="menu-more menu-more-below" onClick={page(1)} data-testid="menu-more-below">
          <IconCaretDown /> {hidden.below} more
        </button>}
    </div>
  )
}
